import {Actions, Button, ButtonGroup, DataStatus, useDialog} from 'platform/components';
import {Box, Hide, HStack, Heading, VStack} from 'platform/foundation';

import {useCallback} from 'react';

import {find, isNil, mergeAll} from 'ramda';

import {useGetVehicleCustomerQuery} from '@dms/api/customer';
import {useGetMetadaServiceCaseQuery} from '@dms/api/workshop';
import {
  DOCUMENT_ACTION_COLUMNS,
  useGetDocumentDatagridActions,
} from '@dms/features/document-context';
import i18n from '@dms/i18n';
import {testIds} from '@dms/routes';
import {DmsDataGrid, UploadDocumentsDialog} from '@dms/shared';

import type {QueryFilterObject} from 'features/eag-grid';

import {useRefreshDocumentCount} from '../../../../../hooks/useRefreshDocumentCount';
import {useWorkshopUrl} from '../../../../../hooks/useWorkshopUrl';

interface ServiceOrderDocumentsProps {
  refreshDataGrid: () => void;
  isReadOnly?: boolean;
}

export function ServiceOrderDocuments(props: ServiceOrderDocumentsProps) {
  const {orderId, serviceCaseId} = useWorkshopUrl();
  const refreshDocumentCount = useRefreshDocumentCount(serviceCaseId);

  const {data: serviceCase} = useGetMetadaServiceCaseQuery({serviceCaseId});

  const {selectedOwnership, isSelectedOwnershipLoading, isSelectedOwnershipError} =
    useGetVehicleCustomerQuery(
      {vehicleId: serviceCase?.vehicleId ?? ''},
      {
        skip: isNil(serviceCase?.vehicleId),
        selectFromResult: ({data, isLoading, isError, isFetching}) => ({
          selectedOwnership: find(
            (ownership) => ownership?.customerType === 'CORVEHICLECUSTOMERTYPE_OPERATOR',
            data ?? []
          ),
          isSelectedOwnershipLoading: isLoading,
          isSelectedOwnershipError: isError,
          isSelectedOwnershipFetching: isFetching,
        }),
      }
    );
  const [actionCallback, bulkSignatureAction] = useGetDocumentDatagridActions({
    refreshData: props.refreshDataGrid,
    contextTarget: 'service-case-order',
    contextId: orderId,
    customerId: selectedOwnership?.customerId,
    isDisabled: props.isReadOnly,
    serviceCaseId,
    serviceOrderId: orderId,
  });

  const [isUploadDialogOpen, openUploadDialog, closeUploadDialog] = useDialog();

  const queryModifier = useCallback(
    (filter: QueryFilterObject) => mergeAll([filter, {serviceCaseOrderId: orderId}]),
    [orderId]
  );

  const handleDocumentCreated = () => {
    props.refreshDataGrid();
    refreshDocumentCount();
  };

  return (
    <VStack>
      <HStack justify="space-between">
        <Heading size={4}>{i18n.t('general.labels.documents')}</Heading>
        <ButtonGroup>
          <Hide when={props.isReadOnly}>
            <Button
              variant="link"
              onClick={openUploadDialog}
              leftIcon="action/backup"
              title={i18n.t('entity.document.actions.uploadDocument')}
              data-testid={testIds.workshop.serviceCases('uploadDocument')}
            />
          </Hide>
          <Actions actions={bulkSignatureAction} />
        </ButtonGroup>
      </HStack>
      <DataStatus
        isLoading={isSelectedOwnershipLoading}
        isError={isSelectedOwnershipError}
        minHeight={33}
      >
        <Box minHeight={40}>
          <DmsDataGrid
            autoHeight
            gridCode="document-context-list-service-case-order"
            actionCallback={actionCallback}
            predefinedColumns={DOCUMENT_ACTION_COLUMNS}
            queryModifier={queryModifier}
            data-testid={testIds.workshop.serviceCases('documentDataGrid')}
          />
        </Box>
      </DataStatus>
      <UploadDocumentsDialog
        isOpen={isUploadDialogOpen}
        handleClose={closeUploadDialog}
        onDocumentCreated={handleDocumentCreated}
        contextTarget="service-case"
        contextId={serviceCaseId}
        bulkContext={[
          {target: 'service-case-order', targetId: orderId},
          {target: 'service-case', targetId: serviceCaseId},
        ]}
      />
    </VStack>
  );
}
